/**
 * One wire item's disclosure, as the sentence the menu card carries
 * (ORDR-121; FR-DSC-001, 003 to 005; FR-RST-002, 003, 011).
 *
 * The text is the framework `MenuItem.description`, which `MenuBrowser` draws
 * inside the item's own button: it is at the point of selection, it is words
 * (never an icon or a colour alone), and it is part of the button's accessible
 * name. In order, each part a sentence:
 *
 *   1. dietary claims, only when there are any   "Vegan, gluten-free."
 *   2. allergens, always                         "Contains: wheat. May contain: sesame.
 *                                                 Declared free from: peanut. Other allergens not recorded."
 *   3. restricted classes, only when declared    "May contain alcohol."
 *   4. an 86's return, only for EIGHTY_SIXED     "Expected back at 14:30." | "No return time given."
 *
 * SILENCE IS NOT ABSENT. `allergens: null` says "not recorded"; an allergen the
 * list does not name, or names with a state this app does not know, is in no
 * list and is covered by "Other allergens not recorded." Only an explicit
 * ABSENT is ever "free from". A dietary `[]` is no claim, and no phrase says
 * "not vegan". An unknown code is SHOWN by its code, and a malformed key says
 * its information is unavailable: dropping either would hide a CONTAINS.
 *
 * The standing direction to contact the location is NOT repeated here: HQ
 * ruling S1 mounts `MenuDisclosure` once above the menu.
 */

import { readWireDisclosure, type Disclosed, type WireAllergen, type WireRestricted } from "./wire.js";

/** Commerce `DietaryTagKind`, lower-cased for mid-sentence use. */
export const DIETARY: Readonly<Record<string, string>> = {
  VEGAN: "vegan",
  VEGETARIAN: "vegetarian",
  GLUTEN_FREE: "gluten-free",
};

/** Commerce `AllergenKind`, all sixteen, lower-cased for mid-sentence use. */
export const ALLERGENS: Readonly<Record<string, string>> = {
  PEANUT: "peanut",
  TREE_NUT: "tree nut",
  SESAME: "sesame",
  MILK: "milk",
  EGG: "egg",
  FISH: "fish",
  CRUSTACEAN: "crustacean",
  MOLLUSC: "mollusc",
  SOY: "soy",
  MUSTARD: "mustard",
  SULPHITES: "added sulphites",
  WHEAT: "wheat",
  BARLEY: "barley",
  RYE: "rye",
  OATS: "oats",
  TRITICALE: "triticale",
};

/** Commerce's restricted classes. The sentences name the substance. */
const RESTRICTED: Readonly<Record<string, string>> = { ALCOHOL: "alcohol" };

/** The allergen states, in the order their sentences are said. Never merged. */
const ALLERGEN_LISTS: readonly (readonly [string, string])[] = [
  ["CONTAINS", "Contains"],
  ["MAY_CONTAIN", "May contain"],
  ["ABSENT", "Declared free from"],
];

export interface DisclosureContext {
  /** BCP 47 tag the restore time is formatted in. */
  readonly language: string;
  readonly now: Date;
  /** Omitted: the device's own zone, which is the diner's. */
  readonly timeZone?: string;
}

function capitalised(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function dietary(tags: Disclosed<string>): string | null {
  if (tags.kind !== "recorded") {
    return "Dietary information unavailable.";
  }
  return tags.value.length === 0 ? null : `${capitalised(tags.value.map((tag) => DIETARY[tag] ?? tag).join(", "))}.`;
}

function allergens(read: Disclosed<WireAllergen>): string {
  if (read.kind === "not-recorded") {
    return "Allergens: not recorded.";
  }
  if (read.kind === "malformed") {
    return "Allergen information unavailable.";
  }
  const parts: string[] = [];
  for (const [state, lead] of ALLERGEN_LISTS) {
    const named = read.value.filter((entry) => entry.state === state).map((entry) => ALLERGENS[entry.allergen] ?? entry.allergen);
    if (named.length > 0) {
      parts.push(`${lead}: ${named.join(", ")}.`);
    }
  }
  const declared = new Set(
    read.value.filter((entry) => entry.allergen in ALLERGENS && ALLERGEN_LISTS.some(([state]) => state === entry.state)).map((entry) => entry.allergen),
  );
  if (declared.size < Object.keys(ALLERGENS).length) {
    parts.push("Other allergens not recorded.");
  }
  return parts.join(" ");
}

function restricted(read: Disclosed<WireRestricted>): string | null {
  if (read.kind === "not-recorded") {
    return null;
  }
  if (read.kind === "malformed") {
    return "Alcohol information unavailable.";
  }
  const parts = read.value.map((entry) => {
    const name = RESTRICTED[entry.class] ?? entry.class;
    switch (entry.state) {
      case "CONTAINS":
        return `Contains ${name}.`;
      case "MAY_CONTAIN":
        return `May contain ${name}.`;
      case "ABSENT":
        return `Declared ${name}-free.`;
      default:
        return `${capitalised(name)} information unavailable.`;
    }
  });
  return parts.join(" ");
}

function returnTime(until: string | null, at: DisclosureContext): string {
  const when = until === null ? Number.NaN : Date.parse(until);
  if (Number.isNaN(when)) {
    return "No return time given.";
  }
  const zone = at.timeZone === undefined ? {} : { timeZone: at.timeZone };
  const time = new Intl.DateTimeFormat(at.language, { hour: "numeric", minute: "2-digit", ...zone }).format(when);
  const day = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", ...zone });
  if (day.format(when) === day.format(at.now)) {
    return `Expected back at ${time}.`;
  }
  const weekday = new Intl.DateTimeFormat(at.language, { weekday: "long", ...zone }).format(when);
  return `Expected back ${weekday} at ${time}.`;
}

/**
 * What the chips (`chips.ts`, ORDR-165) do not carry: restricted classes and an
 * 86's return time, each a sentence; `""` when there is neither. Never throws.
 */
export function describeBeyondChips(raw: unknown, at: DisclosureContext): string {
  const read = readWireDisclosure(raw);
  const availability = read.availability;
  const parts = [
    restricted(read.restricted),
    availability.kind === "recorded" && availability.value.status === "EIGHTY_SIXED"
      ? returnTime(availability.value.unavailable_until, at)
      : null,
  ];
  return parts.filter((part): part is string => part !== null && part !== "").join(" ");
}

/** The disclosure sentence for one wire item. Never throws. */
export function describeItem(raw: unknown, at: DisclosureContext): string {
  const read = readWireDisclosure(raw);
  const availability = read.availability;
  const parts = [
    dietary(read.dietaryTags),
    allergens(read.allergens),
    restricted(read.restricted),
    availability.kind === "recorded" && availability.value.status === "EIGHTY_SIXED"
      ? returnTime(availability.value.unavailable_until, at)
      : null,
  ];
  return parts.filter((part): part is string => part !== null && part !== "").join(" ");
}
